/**
 * useSchoolStudents - Optimized hook for fetching enrolled/withdrawn students
 *
 * This hook provides:
 * - Server-side filtering, sorting, and pagination via RPC
 * - Manages all filter/sort/pagination state internally
 * - Handles district-all mode automatically
 * - Debounced search with optional fuzzy matching
 * - Loading and error states
 * - Request cancellation for rapid filter changes
 * - Response caching with stale-while-revalidate
 * - Next page prefetching
 * - Background auto-refresh
 * - Real-time subscription option for live updates
 * - Infinite scroll mode option
 * - Optimized CSV export for large datasets
 * - Statistics and counts
 *
 * Replaces useStudentQueries for enrolled/withdrawn student tabs
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import { EnhancedErrorLogger } from '@/utils/error-tracking/enhanced-logger';
import {
  SchoolStudentRow,
  TransformedSchoolStudent,
  transformSchoolStudentRow,
  SortField,
  DEFAULT_SCHOOL_STUDENTS_PAGE_SIZE,
} from '@/types/school-students-rpc';

// ============================================================================
// Response Cache
// ============================================================================

interface CacheEntry {
  data: TransformedSchoolStudent[];
  totalCount: number;
  timestamp: number;
}

// Cache TTL in milliseconds (30 seconds) - data is "fresh" for this long
const CACHE_TTL = 30 * 1000;
// Stale TTL - data can be served stale for this long while revalidating (5 minutes)
const STALE_TTL = 5 * 60 * 1000;
// Max cache entries to prevent memory bloat
const MAX_CACHE_ENTRIES = 50;
// Background refresh interval (2 minutes)
const BACKGROUND_REFRESH_INTERVAL = 2 * 60 * 1000;

// In-memory cache for RPC responses
const responseCache = new Map<string, CacheEntry>();

type CacheStatus = 'fresh' | 'stale' | 'expired';

function createCacheKey(params: Record<string, unknown>): string {
  return JSON.stringify(params, Object.keys(params).sort());
}

function getCachedResponse(key: string): { entry: CacheEntry; status: CacheStatus } | null {
  const entry = responseCache.get(key);
  if (!entry) return null;

  const age = Date.now() - entry.timestamp;
  if (age > STALE_TTL) {
    responseCache.delete(key);
    return null;
  }

  const status: CacheStatus = age <= CACHE_TTL ? 'fresh' : 'stale';
  return { entry, status };
}

function setCachedResponse(key: string, data: TransformedSchoolStudent[], totalCount: number): void {
  if (responseCache.size >= MAX_CACHE_ENTRIES) {
    const oldestKey = responseCache.keys().next().value;
    if (oldestKey) responseCache.delete(oldestKey);
  }
  responseCache.set(key, { data, totalCount, timestamp: Date.now() });
}

function clearStudentsCache(): void {
  responseCache.clear();
}

// Export for use in AuthContext logout
export { clearStudentsCache as clearSchoolStudentsCache };

// Debounce helper
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// ============================================================================
// Hook Types
// ============================================================================

export interface UseSchoolStudentsOptions {
  schoolId: string | null;
  academicYear: string;
  status: 'enrolled' | 'withdrawn';
  enabled?: boolean;
  initialPageSize?: number;
  /** Enable real-time subscription for live updates */
  enableRealtime?: boolean;
  /** Use infinite scroll instead of pagination */
  infiniteScroll?: boolean;
}

export interface StudentStatistics {
  total: number;
  byGrade: Record<string, number>;
  withSibling: number;
  needsTransportation: number;
  hasIep: number;
  isEll: number;
  outOfDistrict: number;
}

export interface UseSchoolStudentsReturn {
  // Data
  students: TransformedSchoolStudent[];
  totalCount: number;
  /** Statistics computed from current data (page-level, not total) */
  statistics: StudentStatistics;

  // Loading states
  isLoading: boolean;
  isRefreshing: boolean;
  isBackgroundRefreshing: boolean;
  isLoadingMore: boolean; // For infinite scroll
  error: string | null;

  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  /** Enable fuzzy/trigram search (requires pg_trgm extension) */
  useFuzzySearch: boolean;
  setUseFuzzySearch: (value: boolean) => void;

  // Filters
  gradeFilter: string[];
  setGradeFilter: (grades: string[]) => void;
  hasSiblingFilter: boolean | null;
  setHasSiblingFilter: (value: boolean | null) => void;
  needsTransportFilter: boolean | null;
  setNeedsTransportFilter: (value: boolean | null) => void;
  hasIepFilter: boolean | null;
  setHasIepFilter: (value: boolean | null) => void;
  isEllFilter: boolean | null;
  setIsEllFilter: (value: boolean | null) => void;
  isOutOfDistrictFilter: boolean | null;
  setIsOutOfDistrictFilter: (value: boolean | null) => void;
  cityFilter: string | null;
  setCityFilter: (city: string | null) => void;
  homeDistrictFilter: string | null;
  setHomeDistrictFilter: (district: string | null) => void;

  // Date range filters (for withdrawal queries)
  createdAfter: Date | null;
  setCreatedAfter: (date: Date | null) => void;
  createdBefore: Date | null;
  setCreatedBefore: (date: Date | null) => void;
  withdrawalAfter: Date | null;
  setWithdrawalAfter: (date: Date | null) => void;
  withdrawalBefore: Date | null;
  setWithdrawalBefore: (date: Date | null) => void;

  // Sorting
  sortField: SortField;
  setSortField: (field: SortField) => void;
  sortDirection: 'asc' | 'desc';
  setSortDirection: (direction: 'asc' | 'desc') => void;

  // Pagination
  currentPage: number;
  setCurrentPage: (page: number) => void;
  itemsPerPage: number;
  setItemsPerPage: (count: number) => void;
  totalPages: number;

  // Infinite scroll
  hasMore: boolean;
  loadMore: () => Promise<void>;

  // Actions
  refresh: () => Promise<void>;
  clearCache: () => void;
  resetFilters: () => void;

  // Export
  isExporting: boolean;
  exportProgress: { current: number; total: number };
  exportAllStudents: (onProgress?: (current: number, total: number) => void) => Promise<TransformedSchoolStudent[]>;

  // Real-time
  isRealtimeConnected: boolean;
}

// ============================================================================
// Main Hook
// ============================================================================

export function useSchoolStudents({
  schoolId,
  academicYear,
  status,
  enabled = true,
  initialPageSize = DEFAULT_SCHOOL_STUDENTS_PAGE_SIZE,
  enableRealtime = false,
  infiniteScroll = false,
}: UseSchoolStudentsOptions): UseSchoolStudentsReturn {
  // Data state
  const [students, setStudents] = useState<TransformedSchoolStudent[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isBackgroundRefreshing, setIsBackgroundRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Real-time state
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const realtimeChannelRef = useRef<RealtimeChannel | null>(null);

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });

  // Search state with debouncing
  const [searchQuery, setSearchQuery] = useState('');
  const [useFuzzySearch, setUseFuzzySearch] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Filter state
  const [gradeFilter, setGradeFilter] = useState<string[]>([]);
  const [hasSiblingFilter, setHasSiblingFilter] = useState<boolean | null>(null);
  const [needsTransportFilter, setNeedsTransportFilter] = useState<boolean | null>(null);
  const [hasIepFilter, setHasIepFilter] = useState<boolean | null>(null);
  const [isEllFilter, setIsEllFilter] = useState<boolean | null>(null);
  const [isOutOfDistrictFilter, setIsOutOfDistrictFilter] = useState<boolean | null>(null);
  const [cityFilter, setCityFilter] = useState<string | null>(null);
  const [homeDistrictFilter, setHomeDistrictFilter] = useState<string | null>(null);

  // Date range filters
  const [createdAfter, setCreatedAfter] = useState<Date | null>(null);
  const [createdBefore, setCreatedBefore] = useState<Date | null>(null);
  const [withdrawalAfter, setWithdrawalAfter] = useState<Date | null>(null);
  const [withdrawalBefore, setWithdrawalBefore] = useState<Date | null>(null);

  // Sorting state - persist to localStorage
  const storagePrefix = status === 'enrolled' ? 'enrolledStudents' : 'withdrawnStudents';
  const defaultSortField: SortField = status === 'enrolled' ? 'created_at' : 'withdrawal_date';

  const [sortField, setSortField] = useState<SortField>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(`${storagePrefix}_sortField`);
      return (saved as SortField) || defaultSortField;
    }
    return defaultSortField;
  });

  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(`${storagePrefix}_sortDirection`);
      return saved === 'asc' ? 'asc' : 'desc';
    }
    return 'desc';
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(`${storagePrefix}_itemsPerPage`);
      return saved ? parseInt(saved, 10) : initialPageSize;
    }
    return initialPageSize;
  });

  // Persist sort preferences
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(`${storagePrefix}_sortField`, sortField);
      localStorage.setItem(`${storagePrefix}_sortDirection`, sortDirection);
    }
  }, [sortField, sortDirection, storagePrefix]);

  // Persist pagination preference
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(`${storagePrefix}_itemsPerPage`, String(itemsPerPage));
    }
  }, [itemsPerPage, storagePrefix]);

  const isMountedRef = useRef(true);

  // AbortController for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);
  // Track request ID to ignore stale responses
  const requestIdRef = useRef(0);
  // Track last cache key to prevent redundant state updates
  const lastCacheKeyRef = useRef<string | null>(null);
  // Ref to hold the latest fetchStudents function (prevents stale closure in intervals)
  const fetchStudentsRef = useRef<(showRefreshing?: boolean, skipCache?: boolean) => Promise<void>>();

  // Reset all filters to defaults
  const resetFilters = useCallback(() => {
    setSearchQuery('');
    setUseFuzzySearch(false);
    setGradeFilter([]);
    setHasSiblingFilter(null);
    setNeedsTransportFilter(null);
    setHasIepFilter(null);
    setIsEllFilter(null);
    setIsOutOfDistrictFilter(null);
    setCityFilter(null);
    setHomeDistrictFilter(null);
    setCreatedAfter(null);
    setCreatedBefore(null);
    setWithdrawalAfter(null);
    setWithdrawalBefore(null);
    setCurrentPage(1);
  }, []);

  // Build RPC params
  const buildParams = useCallback((overrideOffset?: number) => {
    const params: Record<string, unknown> = {
      p_academic_year: academicYear,
      p_statuses: [status],
      p_sort_field: sortField,
      p_sort_direction: sortDirection,
      p_limit: itemsPerPage,
      p_offset: overrideOffset ?? (currentPage - 1) * itemsPerPage,
    };

    // School targeting - pass null for district-all, RPC will use user's schools
    if (schoolId && schoolId !== 'district-all') {
      params.p_school_id = schoolId;
    }

    // Search
    if (debouncedSearchQuery.trim()) {
      params.p_search_query = debouncedSearchQuery.trim();
      params.p_use_fuzzy_search = useFuzzySearch;
    }

    // Grade filter
    if (gradeFilter.length > 0) {
      params.p_grades = gradeFilter;
    }

    // Boolean filters
    if (hasSiblingFilter !== null) params.p_has_sibling = hasSiblingFilter;
    if (needsTransportFilter !== null) params.p_needs_transportation = needsTransportFilter;
    if (hasIepFilter !== null) params.p_has_iep = hasIepFilter;
    if (isEllFilter !== null) params.p_is_ell = isEllFilter;
    if (isOutOfDistrictFilter !== null) params.p_is_out_of_district = isOutOfDistrictFilter;

    // Location filters
    if (cityFilter) params.p_city = cityFilter;
    if (homeDistrictFilter) params.p_home_district = homeDistrictFilter;

    // Date range filters
    if (createdAfter) params.p_created_after = createdAfter.toISOString();
    if (createdBefore) params.p_created_before = createdBefore.toISOString();
    if (withdrawalAfter) params.p_withdrawal_after = withdrawalAfter.toISOString().split('T')[0];
    if (withdrawalBefore) params.p_withdrawal_before = withdrawalBefore.toISOString().split('T')[0];

    return params;
  }, [
    schoolId,
    academicYear,
    status,
    debouncedSearchQuery,
    useFuzzySearch,
    gradeFilter,
    hasSiblingFilter,
    needsTransportFilter,
    hasIepFilter,
    isEllFilter,
    isOutOfDistrictFilter,
    cityFilter,
    homeDistrictFilter,
    createdAfter,
    createdBefore,
    withdrawalAfter,
    withdrawalBefore,
    sortField,
    sortDirection,
    currentPage,
    itemsPerPage,
  ]);

  // Compute statistics from current page data
  const statistics = useMemo((): StudentStatistics => {
    const stats: StudentStatistics = {
      total: totalCount,
      byGrade: {},
      withSibling: 0,
      needsTransportation: 0,
      hasIep: 0,
      isEll: 0,
      outOfDistrict: 0,
    };

    students.forEach((student) => {
      // Grade distribution
      const grade = student.applying_grade || 'Unknown';
      stats.byGrade[grade] = (stats.byGrade[grade] || 0) + 1;

      // Boolean counts
      if (student.has_sibling) stats.withSibling++;
      if (student.needs_transportation) stats.needsTransportation++;
      if (student.has_iep) stats.hasIep++;
      if (student.english_language_learner) stats.isEll++;
      if (student.is_out_of_district) stats.outOfDistrict++;
    });

    return stats;
  }, [students, totalCount]);

  // Prefetch next page silently in background
  const prefetchNextPage = useCallback(
    async (currentTotalCount: number) => {
      if (!enabled || !schoolId || !academicYear || infiniteScroll) return;

      const totalPages = Math.ceil(currentTotalCount / itemsPerPage);
      if (currentPage >= totalPages) return;

      const nextPageParams = buildParams(currentPage * itemsPerPage);
      const cacheKey = createCacheKey(nextPageParams);

      if (getCachedResponse(cacheKey)) return;

      try {
        const { data, error: rpcError } = await supabase.rpc('get_school_students', nextPageParams);
        if (rpcError || !isMountedRef.current) return;

        const rows = (data || []) as SchoolStudentRow[];
        const transformed = rows.map(transformSchoolStudentRow);
        const count = rows.length > 0 ? rows[0].total_count : 0;
        setCachedResponse(cacheKey, transformed, count);
      } catch {
        // Silently ignore prefetch errors
      }
    },
    [enabled, schoolId, academicYear, currentPage, itemsPerPage, buildParams, infiniteScroll]
  );

  // Fetch students with request cancellation and caching
  const fetchStudents = useCallback(
    async (showRefreshing = false, skipCache = false) => {
      if (!enabled || !schoolId || !academicYear) {
        return;
      }

      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const currentRequestId = ++requestIdRef.current;

      const params = buildParams();
      const cacheKey = createCacheKey(params);

      // Check cache first (unless explicitly skipping)
      if (!skipCache) {
        const cached = getCachedResponse(cacheKey);
        if (cached) {
          if (lastCacheKeyRef.current !== cacheKey) {
            lastCacheKeyRef.current = cacheKey;
            setStudents(cached.entry.data);
            setTotalCount(cached.entry.totalCount);
          }
          prefetchNextPage(cached.entry.totalCount);

          if (cached.status === 'fresh') {
            return;
          }
        }
      }

      // Show loading state only if no cached data
      if (!getCachedResponse(cacheKey)) {
        if (showRefreshing) {
          setIsRefreshing(true);
        } else {
          setIsLoading(true);
        }
      }
      setError(null);

      try {
        const { data, error: rpcError } = await supabase.rpc('get_school_students', params);

        if (abortController.signal.aborted || currentRequestId !== requestIdRef.current) {
          return;
        }

        if (rpcError) {
          throw rpcError;
        }

        if (isMountedRef.current) {
          const rows = (data || []) as SchoolStudentRow[];
          const transformed = rows.map(transformSchoolStudentRow);
          const count = rows.length > 0 ? rows[0].total_count : 0;

          setCachedResponse(cacheKey, transformed, count);
          lastCacheKeyRef.current = cacheKey;
          setStudents(transformed);
          setTotalCount(count);
          prefetchNextPage(count);
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('Error fetching students:', err);
        EnhancedErrorLogger.getInstance().logQuickError(err instanceof Error ? err : new Error(String(err)), 'SchoolStudents', { context: 'fetch students' });
        if (isMountedRef.current && currentRequestId === requestIdRef.current) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load students';
          setError(errorMessage);
        }
      } finally {
        if (isMountedRef.current && currentRequestId === requestIdRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [enabled, schoolId, academicYear, buildParams, prefetchNextPage]
  );

  // Keep fetchStudents ref updated (for use in intervals without causing recreations)
  fetchStudentsRef.current = fetchStudents;

  // Load more for infinite scroll
  const loadMore = useCallback(async () => {
    if (!infiniteScroll || isLoadingMore || !enabled || !schoolId || !academicYear) {
      return;
    }

    const currentOffset = students.length;
    if (currentOffset >= totalCount) {
      return;
    }

    setIsLoadingMore(true);

    try {
      const params = buildParams(currentOffset);
      const { data, error: rpcError } = await supabase.rpc('get_school_students', params);

      if (rpcError) {
        throw rpcError;
      }

      if (isMountedRef.current) {
        const rows = (data || []) as SchoolStudentRow[];
        const transformed = rows.map(transformSchoolStudentRow);

        setStudents(prev => [...prev, ...transformed]);
      }
    } catch (err) {
      console.error('Error loading more students:', err);
      EnhancedErrorLogger.getInstance().logQuickError(err instanceof Error ? err : new Error(String(err)), 'SchoolStudents', { context: 'update student' });
    } finally {
      if (isMountedRef.current) {
        setIsLoadingMore(false);
      }
    }
  }, [infiniteScroll, isLoadingMore, enabled, schoolId, academicYear, students.length, totalCount, buildParams]);

  const hasMore = infiniteScroll && students.length < totalCount;

  // Refresh function - skips cache to get fresh data
  const refresh = useCallback(async () => {
    if (infiniteScroll) {
      // For infinite scroll, reset and fetch from beginning
      setStudents([]);
      setCurrentPage(1);
    }
    await fetchStudents(true, true);
  }, [fetchStudents, infiniteScroll]);

  // Export all matching students (fetches in chunks, bypasses pagination)
  const exportAllStudents = useCallback(
    async (onProgress?: (current: number, total: number) => void): Promise<TransformedSchoolStudent[]> => {
      if (!enabled || !schoolId || !academicYear) {
        return [];
      }

      setIsExporting(true);
      setExportProgress({ current: 0, total: totalCount });

      const EXPORT_CHUNK_SIZE = 500;
      const allStudents: TransformedSchoolStudent[] = [];
      let offset = 0;
      let fetchedTotal = 0;

      try {
        let hasMore = true;
        while (hasMore) {
          const chunkParams = buildParams(offset);
          chunkParams.p_limit = EXPORT_CHUNK_SIZE;

          const { data, error: rpcError } = await supabase.rpc('get_school_students', chunkParams);

          if (rpcError) {
            throw rpcError;
          }

          const rows = (data || []) as SchoolStudentRow[];
          if (rows.length === 0) {
            hasMore = false;
            continue;
          }

          const transformed = rows.map(transformSchoolStudentRow);
          allStudents.push(...transformed);

          if (fetchedTotal === 0 && rows.length > 0) {
            fetchedTotal = rows[0].total_count;
            setExportProgress({ current: allStudents.length, total: fetchedTotal });
          }

          const progress = { current: allStudents.length, total: fetchedTotal };
          setExportProgress(progress);
          onProgress?.(progress.current, progress.total);

          if (rows.length < EXPORT_CHUNK_SIZE || allStudents.length >= fetchedTotal) {
            hasMore = false;
            continue;
          }

          offset += EXPORT_CHUNK_SIZE;
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        return allStudents;
      } catch (err) {
        console.error('Error exporting students:', err);
        EnhancedErrorLogger.getInstance().logQuickError(err instanceof Error ? err : new Error(String(err)), 'SchoolStudents', { context: 'bulk operation' });
        throw err;
      } finally {
        setIsExporting(false);
        setExportProgress({ current: 0, total: 0 });
      }
    },
    [enabled, schoolId, academicYear, totalCount, buildParams]
  );

  // Auto-fetch on dependency changes
  useEffect(() => {
    if (!infiniteScroll) {
      fetchStudents();
    } else {
      // For infinite scroll, reset data when filters change
      setStudents([]);
      fetchStudents();
    }
  }, [fetchStudents, infiniteScroll]);

  // Reset page when filters change (not for infinite scroll)
  useEffect(() => {
    if (!infiniteScroll) {
      setCurrentPage(1);
    }
  }, [
    debouncedSearchQuery,
    useFuzzySearch,
    gradeFilter,
    hasSiblingFilter,
    needsTransportFilter,
    hasIepFilter,
    isEllFilter,
    isOutOfDistrictFilter,
    cityFilter,
    homeDistrictFilter,
    createdAfter,
    createdBefore,
    withdrawalAfter,
    withdrawalBefore,
    infiniteScroll,
  ]);

  // Real-time subscription
  useEffect(() => {
    if (!enableRealtime || !enabled || !schoolId || !academicYear) {
      return;
    }

    const channelName = `students-${schoolId}-${academicYear}-${status}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'applications',
          filter: schoolId !== 'district-all' ? `placed_school=eq.${schoolId}` : undefined,
        },
        (payload) => {
          // Refresh data on any change
          clearStudentsCache();
          fetchStudents(false, true);
        }
      )
      .subscribe((status) => {
        setIsRealtimeConnected(status === 'SUBSCRIBED');
      });

    realtimeChannelRef.current = channel;

    return () => {
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
      setIsRealtimeConnected(false);
    };
  }, [enableRealtime, enabled, schoolId, academicYear, status, fetchStudents]);

  // Cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Background auto-refresh when tab is visible (disabled if realtime is enabled)
  // Uses ref to avoid recreating interval when fetchStudents changes
  useEffect(() => {
    if (!enabled || enableRealtime) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startBackgroundRefresh = () => {
      if (intervalId) clearInterval(intervalId);

      intervalId = setInterval(async () => {
        if (document.hidden || isLoading || isRefreshing || isExporting) return;

        setIsBackgroundRefreshing(true);
        try {
          // Use ref to get latest function
          await fetchStudentsRef.current?.(false, true);
        } finally {
          if (isMountedRef.current) {
            setIsBackgroundRefreshing(false);
          }
        }
      }, BACKGROUND_REFRESH_INTERVAL);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      } else {
        startBackgroundRefresh();
      }
    };

    startBackgroundRefresh();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, enableRealtime, isLoading, isRefreshing, isExporting]); // Removed fetchStudents - using ref instead

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  return {
    // Data
    students,
    totalCount,
    statistics,

    // Loading states
    isLoading,
    isRefreshing,
    isBackgroundRefreshing,
    isLoadingMore,
    error,

    // Search
    searchQuery,
    setSearchQuery,
    useFuzzySearch,
    setUseFuzzySearch,

    // Filters
    gradeFilter,
    setGradeFilter,
    hasSiblingFilter,
    setHasSiblingFilter,
    needsTransportFilter,
    setNeedsTransportFilter,
    hasIepFilter,
    setHasIepFilter,
    isEllFilter,
    setIsEllFilter,
    isOutOfDistrictFilter,
    setIsOutOfDistrictFilter,
    cityFilter,
    setCityFilter,
    homeDistrictFilter,
    setHomeDistrictFilter,

    // Date range filters
    createdAfter,
    setCreatedAfter,
    createdBefore,
    setCreatedBefore,
    withdrawalAfter,
    setWithdrawalAfter,
    withdrawalBefore,
    setWithdrawalBefore,

    // Sorting
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,

    // Pagination
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,

    // Infinite scroll
    hasMore,
    loadMore,

    // Actions
    refresh,
    clearCache: clearStudentsCache,
    resetFilters,

    // Export
    isExporting,
    exportProgress,
    exportAllStudents,

    // Real-time
    isRealtimeConnected,
  };
}

// ============================================================================
// Convenience Hooks
// ============================================================================

export function useEnrolledStudentsOptimized(
  schoolId: string,
  academicYear: string,
  options: Partial<Omit<UseSchoolStudentsOptions, 'schoolId' | 'academicYear' | 'status'>> = {}
) {
  return useSchoolStudents({
    schoolId,
    academicYear,
    status: 'enrolled',
    ...options,
  });
}

export function useWithdrawnStudentsOptimized(
  schoolId: string,
  academicYear: string,
  options: Partial<Omit<UseSchoolStudentsOptions, 'schoolId' | 'academicYear' | 'status'>> = {}
) {
  return useSchoolStudents({
    schoolId,
    academicYear,
    status: 'withdrawn',
    ...options,
  });
}

export default useSchoolStudents;
